import React, { useState, useEffect, useCallback } from "react";
import styled from "@emotion/styled";
import {
  Card as MuiCard,
  CardContent,
  CardActions,
  Collapse,
  Box,
  Button as MuiButton,
  Checkbox,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  MenuItem,
  Select,
  TextField,
  Typography,
  IconButton,
  FormControlLabel,
  Tooltip,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { spacing, SpacingProps } from "@mui/system";
import agent from "../../utils/axios";

export interface StagingItemProps {
  item: {
    id: string;
    payeeId: string;
    tin: string;
    groupPayeeIds: string[];
    matchedPayeeId: string | null;
    additionalMatchedPayeeIds: string[];
    csiPaymentMethod: string;
    csiCreatedDate: string;
    csiName: string;
    csiEmail: string;
    csiAddress: string;
    tipaltiName: string;
    tipaltiEmail: string;
    tipaltiAddress: string;
    tipaltiPaymentMethod: string;
    inTipalti: boolean | null | undefined;
    isMatchedWithTipalti: boolean | null | undefined;
    matchBasis: string;
    migratedBills: number;
    nonMigratedBills: number;
    analysisCreatedOn: string;
    analysisCompletedOn: string | null;
    analysisCompletedBy: string;
    isPayable: boolean | null | undefined;
    payableReason: string;
    memo: string | null;
    lastModified: string | null;
    lastModifiedBy: string | null;
    reviewedResults: string | null;
    isActive: boolean;
  };
  index: number;
  onTinClick: (tin: string) => void;
  onPayeeIdClick: (payeeId: string) => void;
  onMainPayeeIdClick: (payeeId: string) => void;
  isSelected: boolean;
  onSelect: (id: string) => void;
}

// Styled components
const Card = styled(MuiCard)<SpacingProps & { index: number }>`
  ${spacing}
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
  background-color: ${({ index, theme }) =>
    index % 2 === 0
      ? theme.palette.background.paper
      : theme.palette.background.default};
  color: ${({ theme }) => theme.palette.text.primary};
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  }
  margin-bottom: 8px;
`;
const Button = styled(MuiButton)`
  border-radius: 8px;
  text-transform: none;
  font-weight: 500;
  background-color: ${({ theme }) => theme.palette.primary.main};
  color: ${({ theme }) => theme.palette.primary.contrastText};
  &:hover {
    background-color: ${({ theme }) => theme.palette.primary.dark};
  }
`;
const StyledIconButton = styled(IconButton)`
  transition: transform 0.2s ease-in-out;
  color: ${({ theme }) => theme.palette.text.primary};
  &.expanded {
    transform: rotate(180deg);
  }
`;
const DialogStyled = styled(Dialog)<{ zIndex?: number }>`
  .MuiDialog-paper {
    border-radius: 12px;
    padding: 16px;
    max-width: 600px;
    z-index: ${({ zIndex }) => zIndex || 1300};
    background-color: ${({ theme }) => theme.palette.background.paper};
    color: ${({ theme }) => theme.palette.text.primary};
  }
`;
const SectionHeader = styled(Typography)`
  font-weight: 600;
  margin-bottom: 8px;
  color: ${({ theme }) => theme.palette.primary.main};
`;
const StatusChip = styled(Chip)`
  height: 24px;
  font-size: 0.75rem;
  font-weight: bold;
  margin-left: 8px;
`;
const ClickableSpan = styled("span")`
  cursor: pointer;
  color: ${({ theme }) => theme.palette.primary.main};
  &:hover {
    text-decoration: underline;
  }
`;

const getStatusDescription = (
  value: boolean | null | undefined,
  statusType: string
) => {
  const statusValue =
    value === true ? "true" : value === false ? "false" : "null";

  switch (statusType) {
    case "PAYABLE":
      return `Payable status: ${statusValue} - Indicates if this payee is eligible to receive payments`;
    case "ACTIVE":
      return `Active status: ${statusValue} - Indicates if this payee record is currently active in the system`;
    case "IN TIPALTI":
      return `In Tipalti status: ${statusValue} - Indicates if this payee exists in the Tipalti payment system`;
    case "MATCHED":
      return `Matched status: ${statusValue} - Indicates if this CSI payee has been matched with a Tipalti payee record`;
    default:
      return `${statusType} status is ${statusValue}`;
  }
};

const getGroupPayeeIdsColor = (groupPayeeIds: string[]) => {
  if (!groupPayeeIds || groupPayeeIds.length === 0) return "gold";
  if (groupPayeeIds.length === 1) return "green";
  return "red";
};

const getGroupPayeeIdsDescription = (groupPayeeIds: string[]) => {
  const count = groupPayeeIds ? groupPayeeIds.length : 0;
  return `Group Payee IDs count: ${count} - Shows the number of payee IDs grouped together. Green for 1 ID (good), Red for multiple IDs (needs review), Yellow for none`;
};

function StagingItem({
  item,
  index,
  onTinClick,
  onPayeeIdClick,
  onMainPayeeIdClick,
  isSelected,
  onSelect,
}: StagingItemProps) {
  const [expanded, setExpanded] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [memo, setMemo] = useState("");
  const [reviewedResults, setReviewedResults] = useState("");
  const [isActive, setIsActive] = useState(item.isActive);

  // Initialize local state when item prop changes
  useEffect(() => {
    setMemo(item.memo || "");
    setReviewedResults(item.reviewedResults || "");
    setIsActive(item.isActive);
  }, [item.id, item.memo, item.reviewedResults, item.isActive]);

  const handleSave = async () => {
    const now = new Date().toISOString();
    const updatedItem = {
      ...item,
      memo: memo,
      reviewedResults: reviewedResults,
      lastModified: now,
      lastModifiedBy: "currentUser", // This is Replaced with actual user on the backend
      isActive: isActive,
    };

    try {
      await agent.CSITipalti.updateCSITipalti(item.id, updatedItem);
      setEditMode(false);
    } catch (error) {
      console.error("Error updating staging item:", error);
    }
  };

  // Memoize the memo change handler to prevent recreating the function
  const handleMemoChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setMemo(e.target.value);
    },
    []
  );

  const handleReviewedResultsChange = useCallback((e: any) => {
    setReviewedResults(e.target.value as string);
  }, []);

  const handleActiveChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setIsActive(e.target.checked);
    },
    []
  );

  const getColor = (value: boolean | null | undefined) => {
    if (value === true) return "green";
    if (value === false) return "red";
    return "gold";
  };

  const getValueColor = (value: boolean | null | undefined) => {
    if (value === true) return "#fff";
    if (value === false) return "#fff";
    return "#000";
  };

  const formatTin = (tin: string) => {
    return tin.length > 10 ? `...${tin.slice(-10)}` : tin;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString("en-US", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      timeZoneName: "short",
    });
  };

  // Safely handle boolean or null/undefined values
  const formatStatus = (value: boolean | null | undefined) => {
    if (value === null || value === undefined) return "null";
    return value.toString();
  };

  return (
    <Card m={2} index={index}>
      <CardContent
        sx={{
          py: 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: "pointer",
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Checkbox
            checked={isSelected}
            onChange={() => onSelect(item.id)}
            onClick={(e) => e.stopPropagation()}
          />
          <Box>
            <Typography
              variant="h6"
              color="text.primary"
              sx={{
                width: "280px", // Fixed width for "Yohana Andreina Mendoza Marquina" + 5 chars
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {item.csiName}
            </Typography>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              sx={{
                width: "280px", // Same fixed width for consistency
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              Payee ID: {item.payeeId}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Tooltip title={getStatusDescription(item.inTipalti, "IN TIPALTI")}>
              <StatusChip
                label="IN TIPALTI"
                style={{
                  backgroundColor: getColor(item.inTipalti),
                  color: getValueColor(item.inTipalti),
                }}
              />
            </Tooltip>
            <Tooltip
              title={getStatusDescription(item.isMatchedWithTipalti, "MATCHED")}
            >
              <StatusChip
                label="MATCHED"
                style={{
                  backgroundColor: getColor(item.isMatchedWithTipalti),
                  color: getValueColor(item.isMatchedWithTipalti),
                }}
              />
            </Tooltip>
            <Tooltip title={getStatusDescription(item.isPayable, "PAYABLE")}>
              <StatusChip
                label="PAYABLE"
                style={{
                  backgroundColor: getColor(item.isPayable),
                  color: getValueColor(item.isPayable),
                }}
              />
            </Tooltip>
            <Tooltip title={getStatusDescription(item.isActive, "ACTIVE")}>
              <StatusChip
                label="ACTIVE"
                style={{
                  backgroundColor: getColor(item.isActive),
                  color: getValueColor(item.isActive),
                }}
              />
            </Tooltip>
            <Tooltip title={getGroupPayeeIdsDescription(item.groupPayeeIds)}>
              <StatusChip
                label={`GROUP (${
                  item.groupPayeeIds ? item.groupPayeeIds.length : 0
                })`}
                style={{
                  backgroundColor: getGroupPayeeIdsColor(item.groupPayeeIds),
                  color: "#fff",
                }}
              />
            </Tooltip>
            <Tooltip
              title={`Migrated Bills: ${item.migratedBills} - Shows the number of bills that have been migrated from old system`}
            >
              <StatusChip
                label={`MIGRATED (${item.migratedBills})`}
                style={{
                  backgroundColor: item.migratedBills > 0 ? "blue" : "gray",
                  color: "#fff",
                }}
              />
            </Tooltip>
            <Tooltip
              title={`Non-Migrated Bills: ${item.nonMigratedBills} - Shows the number of bills that are non migrated bills`}
            >
              <StatusChip
                label={`NON-MIG (${item.nonMigratedBills})`}
                style={{
                  backgroundColor:
                    item.nonMigratedBills > 0 ? "orange" : "gray",
                  color: "#fff",
                }}
              />
            </Tooltip>
            <Tooltip
              title={`CSI Payment Method: ${
                item.csiPaymentMethod || "Not specified"
              } - ACH (Green), Check (Yellow), Other (Red)`}
            >
              <StatusChip
                label={`PAY: ${item.csiPaymentMethod || "NONE"}`}
                style={{
                  backgroundColor:
                    item.csiPaymentMethod?.toLowerCase() === "ach"
                      ? "green"
                      : item.csiPaymentMethod?.toLowerCase() === "check"
                      ? "gold"
                      : "red",
                  color:
                    item.csiPaymentMethod?.toLowerCase() === "check"
                      ? "#000" // Black text for yellow/gold background
                      : "#fff", // White text for green/red backgrounds
                }}
              />
            </Tooltip>
          </Box>
        </Box>
        <StyledIconButton
          className={expanded ? "expanded" : ""}
          aria-label="Expand item"
          onClick={(e) => {
            e.stopPropagation();
            setExpanded(!expanded);
          }}
        >
          <ExpandMoreIcon />
        </StyledIconButton>
      </CardContent>
      <Collapse in={expanded} timeout="auto">
        <CardContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {/* CSI Information Section */}
            <Box>
              <SectionHeader variant="subtitle1">CSI Information</SectionHeader>
              <Typography variant="body2">Name: {item.csiName}</Typography>
              <Typography variant="body2">Email: {item.csiEmail}</Typography>
              <Typography variant="body2">
                Address: {item.csiAddress}
              </Typography>
              <Typography variant="body2">
                Payment Method: {item.csiPaymentMethod || "none"}
              </Typography>
              <Typography variant="body2">
                Created Date: {formatDate(item.csiCreatedDate)}
              </Typography>
            </Box>

            {/* Tipalti Information Section */}
            <Box>
              <SectionHeader variant="subtitle1">
                Tipalti Information
              </SectionHeader>
              <Typography variant="body2">Name: {item.tipaltiName}</Typography>
              <Typography variant="body2">
                Email: {item.tipaltiEmail}
              </Typography>
              <Typography variant="body2">
                Address: {item.tipaltiAddress}
              </Typography>
              <Typography variant="body2">
                Payment Method: {item.tipaltiPaymentMethod || "none"}
              </Typography>
            </Box>

            {/* Payee Details Section */}
            <Box>
              <SectionHeader variant="subtitle1">Payee Details</SectionHeader>
              <Typography variant="body2">
                Payee ID:{" "}
                <ClickableSpan
                  onClick={() => onMainPayeeIdClick(item.payeeId)}
                  aria-label="View detailed payee information from CSIPay"
                >
                  {item.payeeId}
                </ClickableSpan>
              </Typography>
              <Typography variant="body2">
                TIN:{" "}
                <ClickableSpan
                  onClick={() => onTinClick(item.tin)}
                  aria-label="View matching TIN records"
                >
                  {formatTin(item.tin)}
                </ClickableSpan>
              </Typography>
              <Typography variant="body2">
                Matched Payee ID:{" "}
                {item.matchedPayeeId ? (
                  <ClickableSpan
                    onClick={() => onPayeeIdClick(item.matchedPayeeId!)}
                    aria-label="View payee details"
                  >
                    {item.matchedPayeeId}
                  </ClickableSpan>
                ) : (
                  "null"
                )}
              </Typography>
              <Typography variant="body2">
                Group Payee IDs:{" "}
                {Array.isArray(item.groupPayeeIds) &&
                item.groupPayeeIds.length > 0
                  ? item.groupPayeeIds.map((id, idx) => (
                      <React.Fragment key={idx}>
                        <ClickableSpan
                          onClick={() => onMainPayeeIdClick(id)}
                          aria-label="View payee details"
                        >
                          {id}
                        </ClickableSpan>
                        {idx < item.groupPayeeIds.length - 1 ? ", " : ""}
                      </React.Fragment>
                    ))
                  : "none"}
              </Typography>
              <Typography variant="body2">
                Additional Matched Payee IDs:{" "}
                {Array.isArray(item.additionalMatchedPayeeIds)
                  ? item.additionalMatchedPayeeIds.join(", ") || "none"
                  : "none"}
              </Typography>
            </Box>

            {/* Analysis Details Section */}
            <Box>
              <SectionHeader variant="subtitle1">
                Analysis Details
              </SectionHeader>
              <Typography variant="body2">
                In Tipalti:{" "}
                <span style={{ color: getColor(item.inTipalti) }}>
                  {formatStatus(item.inTipalti)}
                </span>
              </Typography>
              <Typography variant="body2">
                Is Matched With Tipalti:{" "}
                <span style={{ color: getColor(item.isMatchedWithTipalti) }}>
                  {formatStatus(item.isMatchedWithTipalti)}
                </span>
              </Typography>
              <Typography variant="body2">
                Match Basis: {item.matchBasis}
              </Typography>
              <Typography variant="body2">
                Migrated Bills: {item.migratedBills}
              </Typography>
              <Typography variant="body2">
                Non-Migrated Bills: {item.nonMigratedBills}
              </Typography>
              <Typography variant="body2">
                Analysis Completed By: {item.analysisCompletedBy}
              </Typography>
              <Typography variant="body2">
                Analysis Created On: {formatDate(item.analysisCreatedOn)}
              </Typography>
              <Typography variant="body2">
                Analysis Completed On:{" "}
                {item.analysisCompletedOn
                  ? formatDate(item.analysisCompletedOn)
                  : "N/A"}
              </Typography>
              <Typography variant="body2">
                Is Payable:{" "}
                <span style={{ color: getColor(item.isPayable) }}>
                  {formatStatus(item.isPayable)}
                </span>
              </Typography>
              <Typography variant="body2">
                Payable Reason: {item.payableReason || "none"}
              </Typography>
            </Box>

            {/* Review Information Section */}
            <Box>
              <SectionHeader variant="subtitle1">
                Review Information
              </SectionHeader>
              {editMode ? (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                  <TextField
                    label="Memo"
                    value={memo}
                    onChange={handleMemoChange}
                    fullWidth
                    multiline
                    minRows={3}
                    variant="outlined"
                    size="small"
                  />
                  <Select
                    value={reviewedResults}
                    onChange={handleReviewedResultsChange}
                    fullWidth
                    size="small"
                  >
                    <MenuItem value="">None</MenuItem>
                    <MenuItem value="No Further Action">
                      No Further Action
                    </MenuItem>
                    <MenuItem value="Need Additional Information">
                      Need Additional Information
                    </MenuItem>
                    <MenuItem value="Waiting on Applicant">
                      Waiting on Applicant
                    </MenuItem>
                    <MenuItem value="Duplicate Payee Record">
                      Duplicate Payee Record
                    </MenuItem>
                    <MenuItem value="Pending Review">Pending Review</MenuItem>
                    <MenuItem value="On Hold">On Hold</MenuItem>
                  </Select>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={isActive}
                        onChange={handleActiveChange}
                      />
                    }
                    label="Active"
                  />
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleSave}
                    >
                      Save
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={() => setEditMode(false)}
                    >
                      Cancel
                    </Button>
                  </Box>
                </Box>
              ) : (
                <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                  <Typography variant="body2">
                    Memo: {memo || "none"}
                  </Typography>
                  <Typography variant="body2">
                    Reviewed Results: {reviewedResults || "none"}
                  </Typography>
                  <Typography variant="body2">
                    Last Modified:{" "}
                    {item.lastModified
                      ? formatDateTime(item.lastModified)
                      : "N/A"}
                  </Typography>
                  <Typography variant="body2">
                    Last Modified By: {item.lastModifiedBy || "N/A"}
                  </Typography>
                  <Typography variant="body2">
                    Active: {formatStatus(item.isActive)}
                  </Typography>
                  <Button variant="outlined" onClick={() => setEditMode(true)}>
                    Edit
                  </Button>
                </Box>
              )}
            </Box>
          </Box>
        </CardContent>
        <CardActions>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setOpenModal(true)}
          >
            Show Payee Details
          </Button>
        </CardActions>
      </Collapse>
      <DialogStyled open={openModal} onClose={() => setOpenModal(false)}>
        <DialogTitle sx={{ fontWeight: 500 }}>Payee ID Details</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Typography variant="body2">Payee ID: {item.payeeId}</Typography>
            <Typography variant="body2">
              TIN:{" "}
              <ClickableSpan
                onClick={() => onTinClick(item.tin)}
                aria-label="View matching TIN records"
              >
                {formatTin(item.tin)}
              </ClickableSpan>
            </Typography>
            <Typography variant="body2">
              Matched Payee ID:{" "}
              {item.matchedPayeeId ? (
                <ClickableSpan
                  onClick={() => onPayeeIdClick(item.matchedPayeeId!)}
                  aria-label="View payee details"
                >
                  {item.matchedPayeeId}
                </ClickableSpan>
              ) : (
                "null"
              )}
            </Typography>
            <Typography variant="body2">
              Group Payee IDs:{" "}
              {Array.isArray(item.groupPayeeIds) &&
              item.groupPayeeIds.length > 0
                ? item.groupPayeeIds.map((id, idx) => (
                    <React.Fragment key={idx}>
                      <ClickableSpan
                        onClick={() => onMainPayeeIdClick(id)}
                        aria-label="View payee details"
                      >
                        {id}
                      </ClickableSpan>
                      {idx < item.groupPayeeIds.length - 1 ? ", " : ""}
                    </React.Fragment>
                  ))
                : "none"}
            </Typography>
            <Typography variant="body2">
              Additional Matched Payee IDs:{" "}
              {Array.isArray(item.additionalMatchedPayeeIds)
                ? item.additionalMatchedPayeeIds.join(", ") || "none"
                : "none"}
            </Typography>
            <Typography variant="body2">
              Active: {formatStatus(item.isActive)}
            </Typography>
          </Box>
        </DialogContent>
      </DialogStyled>
    </Card>
  );
}

export default React.memo(StagingItem);
